import React, { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { legacyApi, WorkflowContextResponse } from '../../services/legacyApi';
import { FlowPathBadge } from './FlowPathBadge';
import { FlowActionImpactList } from './FlowActionImpactList';
import { useSettings } from '../../context/SettingsContext';

type FlowCoachPanelProps = {
    entity: string;
    entityId?: number;
    className?: string;
};

const WORKFLOW_CONTEXT_CACHE_TTL_MS = 5000;

type WorkflowContextCacheEntry = {
    expiresAt: number;
    value?: WorkflowContextResponse;
    promise?: Promise<WorkflowContextResponse>;
};

const workflowContextCache = new Map<string, WorkflowContextCacheEntry>();

function getWorkflowContextCached(entity: string, entityId: number): Promise<WorkflowContextResponse> {
    const cacheKey = `${entity}:${entityId}`;
    const now = Date.now();
    const cached = workflowContextCache.get(cacheKey);
    if (cached?.value && cached.expiresAt > now) {
        return Promise.resolve(cached.value);
    }
    if (cached?.promise) {
        return cached.promise;
    }

    const promise = legacyApi.workflow.getContext(entity, entityId)
        .then((result) => {
            workflowContextCache.set(cacheKey, {
                value: result,
                expiresAt: Date.now() + WORKFLOW_CONTEXT_CACHE_TTL_MS,
            });
            return result;
        })
        .catch((error) => {
            workflowContextCache.delete(cacheKey);
            throw error;
        });

    workflowContextCache.set(cacheKey, {
        promise,
        expiresAt: now + WORKFLOW_CONTEXT_CACHE_TTL_MS,
    });
    return promise;
}

const statusTone = (status: string) => {
    const normalized = (status || '').toLowerCase();
    if (normalized === 'fail') return 'text-red-700';
    if (normalized === 'warn') return 'text-amber-700';
    return 'text-emerald-700';
};

export const FlowCoachPanel: React.FC<FlowCoachPanelProps> = ({ entity, entityId, className = '' }) => {
    const { getSetting } = useSettings();
    const flowCoachEnabled = String(getSetting('ui_flow_coach_enabled') || 'true').toLowerCase() !== 'false';
    const [loading, setLoading] = useState(false);
    const [context, setContext] = useState<WorkflowContextResponse | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!entityId) {
            setContext(null);
            return;
        }
        let mounted = true;
        setLoading(true);
        setError(null);
        getWorkflowContextCached(entity, entityId)
            .then((res) => {
                if (!mounted) return;
                setContext(res);
            })
            .catch((err: any) => {
                if (!mounted) return;
                setError(err?.message || 'Failed to load workflow context');
            })
            .finally(() => {
                if (!mounted) return;
                setLoading(false);
            });
        return () => {
            mounted = false;
        };
    }, [entity, entityId]);

    const recommended = useMemo(() => context?.recommended_action || null, [context]);

    if (!flowCoachEnabled || !entityId) return null;
    if (loading) {
        return (
            <div className={`rounded-md border bg-white p-4 ${className}`}>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading flow coach...
                </div>
            </div>
        );
    }
    if (error) {
        return (
            <div className={`rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 ${className}`}>
                {error}
            </div>
        );
    }
    if (!context) return null;

    return (
        <div className={`space-y-3 rounded-md border bg-white p-4 ${className}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Flow Coach</p>
                    <p className="text-sm font-semibold text-slate-900">{context.current_step_label || 'Current Step'}</p>
                </div>
                <FlowPathBadge label={context.path_label} />
            </div>

            <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Current Status</p>
                <p className="text-sm font-medium text-slate-900">{context.current_status_name}</p>
                <p className="text-sm text-slate-600">{context.current_status_meaning || 'No guidance available.'}</p>
            </div>

            {recommended ? (
                <div className="rounded-md border border-blue-200 bg-blue-50 p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Recommended Next Action</p>
                    <p className="text-sm font-semibold text-blue-900">{recommended.event_label}</p>
                    <p className="text-sm text-blue-800">{recommended.event_description || 'No action description.'}</p>
                    <p className={`text-xs font-medium ${statusTone(String(recommended.precheck_status || 'pass'))}`}>
                        Precheck: {(recommended.precheck_status || 'pass').toString().toUpperCase()}
                    </p>
                    <FlowActionImpactList effects={recommended.branch_effect_details || []} />
                </div>
            ) : (
                <p className="text-sm text-muted-foreground">No further actions available for this record.</p>
            )}

            {context.lineage && Object.keys(context.lineage).length > 0 && (
                <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">Source Lineage</p>
                    <div className="mt-1 grid grid-cols-1 gap-1 text-xs text-slate-700">
                        {Object.entries(context.lineage).map(([key, value]) => (
                            <div key={key}>
                                <span className="font-medium">{key.replace(/_/g, ' ')}:</span> {String(value ?? '-')}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {context.handoff_hint ? (
                <p className="text-xs text-slate-600">{context.handoff_hint}</p>
            ) : null}

            {!!context.next_actions?.length && (
                <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Available Actions</p>
                    <ul className="mt-2 space-y-1 text-sm">
                        {context.next_actions.map((action) => (
                            <li key={action.event_code} className="text-slate-700">
                                <span className="font-medium">{action.event_label}</span>
                                {action.to_status_label ? ` -> ${action.to_status_label}` : ''}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};
